<template>
  <v-dialog
    :model-value="modelValue"
    max-width="720px"
    persistent
    data-testid="year-v2-dialog"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <v-card>
      <v-card-title class="bg-primary text-white">
        <v-icon class="mr-2">{{ isEditMode ? 'mdi-calendar-edit' : 'mdi-calendar-plus' }}</v-icon>
        {{ isEditMode ? 'แก้ไขปีภาษี (V2)' : 'เพิ่มปีภาษี (V2)' }}
      </v-card-title>

      <v-card-text class="pt-4">
        <v-form ref="form" v-model="formValid">
          <v-row>
            <v-col cols="12" md="6">
              <v-select
                data-testid="year-v2-be-select"
                v-model="form.beYear"
                :items="yearOptions"
                label="ปี พ.ศ. *"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar"
                :rules="[rules.required]"
                :readonly="isEditMode"
                @update:model-value="onYearChange"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                data-testid="year-v2-taxYearCode-input"
                v-model="form.taxYearCode"
                label="Tax year code *"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-identifier"
                placeholder="TY2569"
                :rules="[rules.required]"
                :readonly="isEditMode"
              />
            </v-col>
          </v-row>

          <v-text-field
            data-testid="year-v2-description-input"
            v-model="form.description"
            label="Description"
            variant="outlined"
            density="comfortable"
            prepend-inner-icon="mdi-text-box-outline"
            placeholder="รายละเอียดปีภาษีนี้"
          />

          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                data-testid="year-v2-startDate-input"
                v-model="form.effectiveStartDate"
                label="Effective start date"
                type="date"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar-start"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                data-testid="year-v2-endDate-input"
                v-model="form.effectiveEndDate"
                label="Effective end date"
                type="date"
                variant="outlined"
                density="comfortable"
                prepend-inner-icon="mdi-calendar-end"
              />
            </v-col>
          </v-row>

          <v-switch
            data-testid="year-v2-active-switch"
            v-model="form.isActive"
            label="Active (หลายปีเปิดพร้อมกันได้)"
            color="success"
            inset
            hide-details
          />
        </v-form>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn data-testid="year-v2-cancel-btn" variant="outlined" @click="close">
          <v-icon start>mdi-close</v-icon>
          Cancel
        </v-btn>
        <v-btn
          data-testid="year-v2-submit-btn"
          color="primary"
          variant="elevated"
          :disabled="!formValid"
          @click="submit"
        >
          <v-icon start>mdi-content-save</v-icon>
          {{ isEditMode ? 'Update' : 'Create' }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'TaxYearV2Dialog',
  props: {
    modelValue: { type: Boolean, default: false },
    yearOptions: { type: Array, default: () => [] },
    initialData: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'submit'],
  data() {
    return {
      formValid: false,
      form: this.buildEmptyForm(),
      rules: {
        required: (v) => (v !== null && v !== undefined && String(v).trim() !== '') || 'Required',
      },
    };
  },
  computed: {
    isEditMode() {
      // In edit mode the parent passes an existing year that carries yearID.
      return Boolean(this.initialData && this.initialData.yearID != null);
    },
  },
  watch: {
    modelValue(open) {
      if (open) this.resetForm();
    },
  },
  methods: {
    buildEmptyForm() {
      const beYear = new Date().getFullYear() + 543 + 1;
      const ceYear = beYear - 543;
      return {
        yearID: null,
        beYear,
        year: ceYear,
        taxYearCode: `TY${beYear}`,
        description: '',
        effectiveStartDate: `${ceYear}-01-01`,
        effectiveEndDate: `${ceYear}-12-31`,
        isActive: true,
      };
    },
    resetForm() {
      if (this.initialData) {
        const d = this.initialData;
        this.form = {
          yearID: d.yearID ?? null,
          beYear: d.beYear ?? (d.year != null ? d.year + 543 : null),
          year: d.year ?? (d.beYear != null ? d.beYear - 543 : null),
          taxYearCode: d.taxYearCode || '',
          description: d.description || '',
          effectiveStartDate: (d.effectiveStartDate || '').slice(0, 10),
          effectiveEndDate: (d.effectiveEndDate || '').slice(0, 10),
          isActive: d.isActive ?? true,
        };
      } else {
        this.form = this.buildEmptyForm();
      }
      this.$nextTick(() => this.$refs.form?.resetValidation?.());
    },
    onYearChange(beYear) {
      const ceYear = Number(beYear) - 543;
      this.form.beYear = Number(beYear);
      this.form.year = ceYear;
      this.form.taxYearCode = `TY${beYear}`;
      this.form.effectiveStartDate = `${ceYear}-01-01`;
      this.form.effectiveEndDate = `${ceYear}-12-31`;
    },
    close() {
      this.$emit('update:modelValue', false);
    },
    async submit() {
      const result = await this.$refs.form?.validate?.();
      if (result && result.valid === false) return;
      this.$emit('submit', {
        yearID: this.form.yearID,
        beYear: Number(this.form.beYear),
        year: Number(this.form.year),
        taxYearCode: String(this.form.taxYearCode || '').trim().toUpperCase(),
        description: this.form.description || '',
        effectiveStartDate: this.form.effectiveStartDate || null,
        effectiveEndDate: this.form.effectiveEndDate || null,
        isActive: Boolean(this.form.isActive),
      });
    },
  },
};
</script>
